import { InjectionToken } from '@angular/core';
import { type ActionCreator, type ActionReducer, type ReducerTypes } from '@ngrx/store';

import {
  composeEngineReducer,
  type EngineStateBase,
  type EngineStateExtension,
  fromChat,
} from '@blaze-runtime/engine-data-access';

import { aiReducerTypes } from '../ai/ai.reducer';
import { chartReducerTypes } from '../chart/chart.reducer';
import { copyPasteReducerTypes } from '../copy-paste/copy-paste.reducer';
import { elementsReducerTypes } from '../elements';
import { generationReducerTypes } from '../generation/generation.reducer';
import { navMenuReducerTypes } from '../nav-menu/nav-menu.reducer';
import { runtimeReducerTypes } from '../runtime/runtime.reducer';
import { runtimeErrorsReducerTypes } from '../runtime-errors/runtime-errors.reducer';

import { initialEngineState } from './engine.state';

/**
 * The handlers the engine registers itself, in the order they must run — `elements` and `runtime`
 * both handle five of the same action types, so the order is behaviour, not formatting.
 */
const engineReducerTypes: readonly ReducerTypes<EngineStateBase, ActionCreator[]>[] = [
  ...elementsReducerTypes,
  ...runtimeReducerTypes,
  ...generationReducerTypes,
  ...navMenuReducerTypes,
  ...copyPasteReducerTypes,
  ...fromChat.chatReducerTypes,
  ...chartReducerTypes,
  ...aiReducerTypes,
  ...runtimeErrorsReducerTypes,
];

/**
 * The assembled `engine` feature reducer: the slices above plus every registered
 * {@link EngineStateExtension}. `EngineModule` provides it; nothing else should read it.
 */
export const ENGINE_REDUCER = new InjectionToken<ActionReducer<EngineStateBase>>('Engine reducer');

export function createEngineReducer(
  extensions: readonly EngineStateExtension[] | null,
): ActionReducer<EngineStateBase> {
  return composeEngineReducer(initialEngineState, engineReducerTypes, extensions ?? []);
}

/**
 * The engine's own slices with no extension registered — what a published runtime reduces with.
 * Tests and the rendering corpus use it directly; the store gets {@link ENGINE_REDUCER}.
 */
export const engineReducer: ActionReducer<EngineStateBase> = createEngineReducer(null);
